import { Link, NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Globe } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

export default function Navbar() {
  const { lang, toggle, t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const leftItems = [
    { to: "/events", label: t.nav.events, id: "events" },
    { to: "/gallery", label: t.nav.gallery, id: "gallery" },
  ];
  const rightItems = [
    { to: "/ambassadors", label: t.nav.merch, id: "ambassadors" },
    { to: "/contact", label: t.nav.contact, id: "contact" },
    { to: "/about", label: t.nav.about, id: "about" },
  ];

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "py-3 bg-black/60 backdrop-blur-xl border-b border-white/5"
            : "py-5 bg-transparent"
        }`}
        data-testid="navbar"
      >
        <div className="container-sv flex items-center justify-between">
          {/* LEFT */}
          <nav className="hidden md:flex items-center gap-8 flex-1">
            {leftItems.map((it) => (
              <NavLink
                key={it.id}
                to={it.to}
                data-testid={`nav-link-${it.id}`}
                className={({ isActive }) =>
                  `nav-link font-display font-medium text-[11px] uppercase tracking-[0.28em] ${
                    isActive ? "is-active text-white" : ""
                  }`
                }
              >
                {it.label}
              </NavLink>
            ))}
          </nav>

          {/* CENTER LOGO */}
          <Link
            to="/"
            data-testid="logo-link"
            className="flex-shrink-0 group"
            onClick={() => setOpen(false)}
          >
            <img
              src="/brand/seoulvibes-logo.png?v=2"
              alt="SeoulVibes"
              className="h-12 md:h-14 w-auto transition-transform duration-500 group-hover:scale-105"
              style={{ filter: "drop-shadow(0 0 18px rgba(236,72,153,0.55))" }}
            />
          </Link>

          {/* RIGHT */}
          <nav className="hidden md:flex items-center gap-8 flex-1 justify-end">
            {rightItems.map((it) => (
              <NavLink
                key={it.id}
                to={it.to}
                data-testid={`nav-link-${it.id}`}
                className={({ isActive }) =>
                  `nav-link font-display font-medium text-[11px] uppercase tracking-[0.28em] ${
                    isActive ? "is-active text-white" : ""
                  }`
                }
              >
                {it.label}
              </NavLink>
            ))}
            <button
              onClick={toggle}
              data-testid="language-toggle"
              className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.28em] font-display font-medium text-white/70 hover:text-white transition-colors border border-white/10 rounded-full px-3 py-1.5 hover:border-purple-400/60"
              aria-label="Toggle language"
            >
              <Globe className="w-3 h-3" />
              <span data-testid="language-current">
                {lang === "en" ? "EN" : "CZ"}
              </span>
            </button>
          </nav>

          {/* MOBILE */}
          <div className="md:hidden flex items-center gap-2">
            <button
              onClick={toggle}
              data-testid="language-toggle-mobile"
              className="text-[11px] uppercase tracking-[0.2em] font-display font-medium text-white/80 border border-white/10 rounded-full px-2.5 py-1"
            >
              {lang.toUpperCase()}
            </button>
            <button
              onClick={() => setOpen((p) => !p)}
              data-testid="hamburger-toggle"
              aria-label="Menu"
              className="w-10 h-10 flex items-center justify-center text-white"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* MOBILE MENU */}
      <div
        className={`md:hidden fixed inset-0 z-40 bg-black/95 backdrop-blur-2xl transition-all duration-500 ${
          open
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
        data-testid="mobile-menu"
      >
        <div className="h-full flex flex-col items-center justify-center gap-8 px-8">
          {[...leftItems, ...rightItems].map((it, i) => (
            <NavLink
              key={it.id}
              to={it.to}
              onClick={() => setOpen(false)}
              data-testid={`mobile-nav-link-${it.id}`}
              className={({ isActive }) =>
                `font-display font-bold text-3xl uppercase tracking-tight transition-colors ${
                  isActive ? "text-neon-purple" : "text-white hover:text-neon-purple"
                }`
              }
              style={{ animationDelay: `${i * 60}ms` }}
            >
              {it.label}
            </NavLink>
          ))}
          <div className="mt-8 text-white/40 text-xs uppercase tracking-[0.3em] font-display">
            From Seoul to your city.
          </div>
        </div>
      </div>
    </>
  );
}
